// ============================================================
// src/context/MusicPlayerContext.js
//
// Global audio player that lives outside the router so music
// keeps playing when navigating between pages.
//
// ── ADDED IN THIS VERSION ────────────────────────────────────
//   queue / setQueue  — load a full setlist array
//   nextTrack         — advance to next in queue
//   prevTrack         — go back (or restart if >3s in)
//   cycleRepeat       — rotate none -> all -> one -> none
//   repeatMode        — 'none' | 'all' | 'one'
//   queueIndex        — current position in queue
//   shuffleMode       — boolean, randomises playback order
//   toggleShuffle     — flip shuffleMode on/off
//   Media Session API — enables lock screen controls and
//                       Now Playing info on iOS/Android.
//                       Music continues playing when the screen
//                       locks and auto-advances to the next track.
// ── HOW TO USE ───────────────────────────────────────────────
//   Wrap app with <MusicPlayerProvider> in App.js
//   const { playTrack, setQueue, nextTrack, ... } = useMusicPlayer()
// ============================================================

import { createContext, useContext, useRef, useState, useCallback, useEffect } from 'react'

const MusicPlayerContext = createContext(null)

export function MusicPlayerProvider({ children }) {

  const audioRef = useRef(new Audio())

  const [currentTrack, setCurrentTrack] = useState(null)
  const [isPlaying,    setIsPlaying]    = useState(false)
  const [currentTime,  setCurrentTime]  = useState(0)
  const [duration,     setDuration]     = useState(0)
  const [progress,     setProgress]     = useState(0)
  const [queue,        setQueueState]   = useState([])
  const [queueIndex,   setQueueIndex]   = useState(-1)
  const [repeatMode,   setRepeatMode]   = useState('none')
  // repeatMode: 'none' = stop at end | 'all' = loop queue | 'one' = loop track

  const [shuffleMode, setShuffleMode] = useState(false)
  // shuffleMode: when true, the next track is chosen randomly
  // from the remaining tracks in the queue rather than linearly.
  // originalQueue stores the pre-shuffle order so we can restore
  // it when the user turns shuffle off.

  // Refs keep queue/index/repeat/shuffle fresh inside audio event
  // callbacks without causing stale closures in useCallback.
  const queueRef       = useRef([])
  const queueIndexRef  = useRef(-1)
  const repeatModeRef  = useRef('none')
  const shuffleModeRef = useRef(false)
  const originalQueueRef = useRef([])
  // originalQueueRef stores the unshuffled queue so we can restore
  // playback order when the user turns shuffle off mid-session.

  useEffect(() => { queueRef.current       = queue       }, [queue])
  useEffect(() => { queueIndexRef.current  = queueIndex  }, [queueIndex])
  useEffect(() => { repeatModeRef.current  = repeatMode  }, [repeatMode])
  useEffect(() => { shuffleModeRef.current = shuffleMode }, [shuffleMode])


  // ── shuffleArray ──────────────────────────────────────────
  // Fisher-Yates shuffle — returns a NEW array, never mutates.
  function shuffleArray(arr) {
    const a = [...arr]
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      ;[a[i], a[j]] = [a[j], a[i]]
    }
    return a
  }


  // ── updateMediaSession ────────────────────────────────────
  // Pushes Now Playing metadata and action handlers to the
  // browser's Media Session API. This is what appears on:
  //   • iOS lock screen / Control Center
  //   • Android lock screen / notification shade
  //   • macOS Touch Bar / media keys
  //   • Desktop browser media controls
  //
  // Must be called every time the current track changes so
  // the lock screen shows the correct title.
  // The action handlers mirror our internal playback controls
  // so hardware buttons (headphone remote, lock screen buttons)
  // work even when the app is in the background.
  const updateMediaSession = useCallback((track) => {
    if (!('mediaSession' in navigator)) return
    // mediaSession is not available in all environments (e.g. some
    // older Android browsers) so we guard before using it.

    if (track) {
      navigator.mediaSession.metadata = new MediaMetadata({
        title:  track.name,
        artist: 'Drag Pocket Planner',
        // album:  '' — we don't have album info, leave blank
        // artwork is optional and requires absolute HTTPS URLs;
        // omitting it shows a generic music icon on the lock screen
        // which is fine for a drag performer tool.
      })
    } else {
      // No track playing — clear the session metadata
      navigator.mediaSession.metadata = null
    }
  }, [])

  // ── registerMediaSessionHandlers ─────────────────────────
  // Registers the action handlers once on mount so the lock
  // screen buttons always route to the latest playback functions.
  // We use refs inside the callbacks so they always see the
  // current queue state without needing to re-register.
  //
  // IMPORTANT: these must be set before the first play() call
  // or iOS may not show the lock screen controls at all.
  useEffect(() => {
    if (!('mediaSession' in navigator)) return

    navigator.mediaSession.setActionHandler('play', () => {
      // Lock screen "play" button
      audioRef.current.play()
        .then(() => setIsPlaying(true))
        .catch(console.error)
    })

    navigator.mediaSession.setActionHandler('pause', () => {
      // Lock screen "pause" button
      audioRef.current.pause()
      setIsPlaying(false)
    })

    navigator.mediaSession.setActionHandler('nexttrack', () => {
      // Lock screen "next" button — same logic as nextTrack()
      const q   = queueRef.current
      const idx = queueIndexRef.current
      if (!q.length) return

      if (shuffleModeRef.current) {
        // Shuffle: pick a random index that isn't the current one
        let next = Math.floor(Math.random() * q.length)
        if (q.length > 1 && next === idx) next = (next + 1) % q.length
        setQueueIndex(next)
        loadAndPlayRef.current(q[next])
      } else {
        const next = idx + 1
        if (next < q.length) {
          setQueueIndex(next)
          loadAndPlayRef.current(q[next])
        } else if (repeatModeRef.current === 'all') {
          setQueueIndex(0)
          loadAndPlayRef.current(q[0])
        }
      }
    })

    navigator.mediaSession.setActionHandler('previoustrack', () => {
      // Lock screen "previous" button — same logic as prevTrack()
      const audio = audioRef.current
      const q     = queueRef.current
      const idx   = queueIndexRef.current
      if (!q.length) return

      if (audio.currentTime > 3) {
        audio.currentTime = 0
        return
      }
      const prev = idx - 1
      if (prev >= 0) {
        setQueueIndex(prev)
        loadAndPlayRef.current(q[prev])
      } else if (repeatModeRef.current === 'all') {
        setQueueIndex(q.length - 1)
        loadAndPlayRef.current(q[q.length - 1])
      }
    })

    // seekbackward / seekforward — optional but nice for headphone remotes
    navigator.mediaSession.setActionHandler('seekbackward', (details) => {
      const audio  = audioRef.current
      const offset = details.seekOffset || 10
      audio.currentTime = Math.max(0, audio.currentTime - offset)
    })

    navigator.mediaSession.setActionHandler('seekforward', (details) => {
      const audio  = audioRef.current
      const offset = details.seekOffset || 10
      audio.currentTime = Math.min(audio.duration || 0, audio.currentTime + offset)
    })

    // seekto — enables scrubbing from the lock screen progress bar
    navigator.mediaSession.setActionHandler('seekto', (details) => {
      if (details.seekTime !== undefined) {
        audioRef.current.currentTime = details.seekTime
      }
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  // [] = register handlers once on mount; refs provide fresh values


  // ── loadAndPlayRef ────────────────────────────────────────
  // We need loadAndPlay accessible inside the Media Session
  // action handlers (which are registered once on mount) but
  // loadAndPlay itself uses useCallback. The solution is to
  // keep a stable ref to the latest version of loadAndPlay.
  const loadAndPlayRef = useRef(null)


  // ── Internal: load a track into the audio element and play ─
  const loadAndPlay = useCallback((track) => {
    const audio = audioRef.current
    audio.src = track.url
    audio.currentTime = 0
    setCurrentTrack(track)
    setCurrentTime(0)
    setProgress(0)
    setDuration(0)

    // Update lock screen / Now Playing info immediately so the
    // title changes before the audio actually starts loading.
    updateMediaSession(track)

    audio.play()
      .then(() => {
        setIsPlaying(true)
        // Update playback state on the Media Session so the lock
        // screen shows "playing" rather than "paused".
        if ('mediaSession' in navigator) {
          navigator.mediaSession.playbackState = 'playing'
        }
      })
      .catch((err) => {
        console.error('MusicPlayer: play() blocked:', err)
        setIsPlaying(false)
        if ('mediaSession' in navigator) {
          navigator.mediaSession.playbackState = 'none'
        }
      })
  }, [updateMediaSession])

  // Keep the ref in sync with the latest loadAndPlay
  useEffect(() => { loadAndPlayRef.current = loadAndPlay }, [loadAndPlay])


  // ── Audio event listeners ─────────────────────────────────
  useEffect(() => {
    const audio = audioRef.current

    const onTimeUpdate = () => {
      setCurrentTime(audio.currentTime)
      if (audio.duration > 0) {
        setProgress((audio.currentTime / audio.duration) * 100)

        // Update Media Session position state so the lock screen
        // progress bar scrubs correctly.
        if ('mediaSession' in navigator && navigator.mediaSession.setPositionState) {
          try {
            navigator.mediaSession.setPositionState({
              duration:     audio.duration,
              playbackRate: audio.playbackRate,
              position:     audio.currentTime,
            })
          } catch {
            // setPositionState can throw if duration is Infinity (live stream)
          }
        }
      }
    }

    const onLoadedMetadata = () => setDuration(audio.duration)

    const onEnded = () => {
  // ── Use loadAndPlayRef.current instead of loadAndPlay directly ──
  // loadAndPlay is a useCallback that changes identity when its deps
  // change. onEnded is registered once; if it closes over the initial
  // loadAndPlay it becomes stale. loadAndPlayRef always points to the
  // latest version so background/lock-screen auto-advance works.
  const play = loadAndPlayRef.current
  if (!play) return

  const q    = queueRef.current
  const idx  = queueIndexRef.current
  const mode = repeatModeRef.current
  const shuf = shuffleModeRef.current

  if (mode === 'one') {
    // Replay the same track
    audioRef.current.currentTime = 0
    audioRef.current.play().then(() => setIsPlaying(true)).catch(console.error)
    return
  }

  if (q.length > 0) {
    if (shuf) {
      let next = Math.floor(Math.random() * q.length)
      if (q.length > 1 && next === idx) next = (next + 1) % q.length
      setQueueIndex(next)
      play(q[next])
    } else {
      const next = idx + 1
      if (next < q.length) {
        setQueueIndex(next)
        play(q[next])
      } else if (mode === 'all') {
        setQueueIndex(0)
        play(q[0])
      } else {
        setIsPlaying(false)
        setCurrentTime(0)
        setProgress(0)
        if ('mediaSession' in navigator) {
          navigator.mediaSession.playbackState = 'none'
        }
      }
    }
  } else {
    setIsPlaying(false)
    setCurrentTime(0)
    setProgress(0)
    if ('mediaSession' in navigator) {
      navigator.mediaSession.playbackState = 'none'
    }
  }
}

    const onPause = () => {
      if ('mediaSession' in navigator) {
        navigator.mediaSession.playbackState = 'paused'
      }
    }

    const onPlay = () => {
      if ('mediaSession' in navigator) {
        navigator.mediaSession.playbackState = 'playing'
      }
    }

    const onError = () => {
      // ── FIX: ignore the spurious error fired when stop() clears the src ──
      // When stop() calls audio.removeAttribute('src') + audio.load(), some
      // browsers still fire the error event during the reset. We guard against
      // it by checking whether src is empty or resolves to the current page URL
      // (which is what browsers do when they try to load an empty string as audio).
      // If either is true this is an intentional reset, not a real load failure,
      // so we return early without touching any state.
      if (!audio.src || audio.src === window.location.href) return

      console.error('MusicPlayer: failed to load:', audio.src)
      setIsPlaying(false)
      setCurrentTrack(null)
    }

    audio.addEventListener('timeupdate',     onTimeUpdate)
    audio.addEventListener('loadedmetadata', onLoadedMetadata)
    audio.addEventListener('ended',          onEnded)
    audio.addEventListener('pause',          onPause)
    audio.addEventListener('play',           onPlay)
    audio.addEventListener('error',          onError)

    return () => {
      audio.removeEventListener('timeupdate',     onTimeUpdate)
      audio.removeEventListener('loadedmetadata', onLoadedMetadata)
      audio.removeEventListener('ended',          onEnded)
      audio.removeEventListener('pause',          onPause)
      audio.removeEventListener('play',           onPlay)
      audio.removeEventListener('error',          onError)
    }
  }, [])


  // ── playTrack ─────────────────────────────────────────────
  // Play a single track, clearing any queue
  const playTrack = useCallback((track) => {
    if (currentTrack?.url === track.url) {
      audioRef.current.play()
      setIsPlaying(true)
      return
    }
    setQueueState([])
    setQueueIndex(-1)
    originalQueueRef.current = []
    loadAndPlay(track)
  }, [currentTrack, loadAndPlay])


  // ── setQueue ──────────────────────────────────────────────
  // Load a full setlist array and start at the given index.
  // If shuffle is on, the queue is shuffled immediately with
  // the chosen track placed first so it plays right away.
  const setQueue = useCallback((tracks, startIndex = 0) => {
    if (!tracks?.length) return

    originalQueueRef.current = tracks
    // save original order so we can restore it if shuffle is toggled off

    if (shuffleModeRef.current) {
      // Build a shuffled queue with the chosen track at position 0
      const chosen  = tracks[startIndex]
      const rest    = tracks.filter((_, i) => i !== startIndex)
      const shuffled = [chosen, ...shuffleArray(rest)]
      setQueueState(shuffled)
      setQueueIndex(0)
      loadAndPlay(shuffled[0])
    } else {
      setQueueState(tracks)
      setQueueIndex(startIndex)
      loadAndPlay(tracks[startIndex])
    }
  }, [loadAndPlay])


  // ── nextTrack ─────────────────────────────────────────────
  // Skip to the next track. In shuffle mode picks randomly.
  // Wraps in repeat-all mode.
  const nextTrack = useCallback(() => {
    const q   = queueRef.current
    const idx = queueIndexRef.current
    if (!q.length) return

    if (shuffleModeRef.current) {
      let next = Math.floor(Math.random() * q.length)
      if (q.length > 1 && next === idx) next = (next + 1) % q.length
      setQueueIndex(next)
      loadAndPlay(q[next])
    } else {
      const next = idx + 1
      if (next < q.length) {
        setQueueIndex(next)
        loadAndPlay(q[next])
      } else if (repeatModeRef.current === 'all') {
        setQueueIndex(0)
        loadAndPlay(q[0])
      }
    }
  }, [loadAndPlay])


  // ── prevTrack ─────────────────────────────────────────────
  // Go back one track. If >3s into the current track, restart it
  // instead (Spotify behaviour). Wraps in repeat-all mode.
  const prevTrack = useCallback(() => {
    const audio = audioRef.current
    const q     = queueRef.current
    const idx   = queueIndexRef.current
    if (!q.length) return

    if (audio.currentTime > 3) {
      // Restart current track rather than going back
      audio.currentTime = 0
      return
    }

    const prev = idx - 1
    if (prev >= 0) {
      setQueueIndex(prev)
      loadAndPlay(q[prev])
    } else if (repeatModeRef.current === 'all') {
      setQueueIndex(q.length - 1)
      loadAndPlay(q[q.length - 1])
    }
  }, [loadAndPlay])


  // ── togglePlay ────────────────────────────────────────────
  const togglePlay = useCallback(() => {
    const audio = audioRef.current
    if (!currentTrack) return
    if (isPlaying) {
      audio.pause()
      setIsPlaying(false)
    } else {
      audio.play().then(() => setIsPlaying(true)).catch(console.error)
    }
  }, [isPlaying, currentTrack])


  // ── stop ──────────────────────────────────────────────────
  // Pauses and fully resets the audio element.
  //
  // WHY removeAttribute('src') + load() instead of audio.src = '':
  //   Setting audio.src = '' assigns an empty string which browsers
  //   interpret as a RELATIVE URL, resolving it to the current page URL.
  //   The browser then tries to fetch the HTML page as audio, fails,
  //   and fires the 'error' event unnecessarily.
  //   removeAttribute('src') fully removes the src attribute so the
  //   element has no source at all. load() then resets internal state.
  //   This is the correct W3C-spec way to reset an HTMLMediaElement.
  const stop = useCallback(() => {
    const audio = audioRef.current
    audio.pause()
    audio.removeAttribute('src')
    audio.load()
    setIsPlaying(false)
    setCurrentTrack(null)
    setCurrentTime(0)
    setProgress(0)
    setDuration(0)
    setQueueState([])
    setQueueIndex(-1)
    originalQueueRef.current = []
    updateMediaSession(null)
    if ('mediaSession' in navigator) {
      navigator.mediaSession.playbackState = 'none'
    }
  }, [updateMediaSession])


  // ── seek ──────────────────────────────────────────────────
  const seek = useCallback((percentage) => {
    const audio = audioRef.current
    if (!audio.duration) return
    audio.currentTime = (percentage / 100) * audio.duration
  }, [])


  // ── cycleRepeat ───────────────────────────────────────────
  const cycleRepeat = useCallback(() => {
    setRepeatMode(prev => {
      if (prev === 'none') return 'all'
      if (prev === 'all')  return 'one'
      return 'none'
    })
  }, [])


  // ── toggleShuffle ─────────────────────────────────────────
  // Flips shuffleMode on/off.
  //
  // Turning shuffle ON:  re-orders the current queue randomly,
  //   keeping the current track in place at queueIndex 0 so
  //   it keeps playing without interruption.
  //
  // Turning shuffle OFF: restores the original queue order and
  //   finds the current track's position in that original order
  //   so queueIndex stays correct.
  const toggleShuffle = useCallback(() => {
    setShuffleMode(prev => {
      const next = !prev

      if (next) {
        // Turning shuffle ON
        const q       = queueRef.current
        const idx     = queueIndexRef.current
        const current = q[idx]

        if (q.length > 1 && current) {
          // Save original order if we haven't already
          if (!originalQueueRef.current.length) {
            originalQueueRef.current = [...q]
          }
          const rest     = q.filter((_, i) => i !== idx)
          const shuffled = [current, ...shuffleArray(rest)]
          setQueueState(shuffled)
          setQueueIndex(0)
          // Current track stays at position 0 — no interruption
        }
      } else {
        // Turning shuffle OFF — restore original queue order
        const orig    = originalQueueRef.current
        const current = queueRef.current[queueIndexRef.current]

        if (orig.length && current) {
          // Find where the current track sits in the original order
          const restoredIdx = orig.findIndex(t => t.url === current.url)
          setQueueState(orig)
          setQueueIndex(restoredIdx >= 0 ? restoredIdx : 0)
        }
      }

      return next
    })
  }, [])


  const value = {
    currentTrack,
    isPlaying,
    progress,
    currentTime,
    duration,
    queue,
    queueIndex,
    repeatMode,
    shuffleMode,
    playTrack,
    setQueue,
    togglePlay,
    stop,
    seek,
    nextTrack,
    prevTrack,
    cycleRepeat,
    toggleShuffle,
  }

  return (
    <MusicPlayerContext.Provider value={value}>
      {children}
    </MusicPlayerContext.Provider>
  )
}

export function useMusicPlayer() {
  const ctx = useContext(MusicPlayerContext)
  if (!ctx) throw new Error('useMusicPlayer must be used inside <MusicPlayerProvider>')
  return ctx
}